import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const timeSlots = [];
for (let h = 6; h <= 20; h++) {
  timeSlots.push(`${String(h).padStart(2, '0')}:00`);
  if (h < 20) timeSlots.push(`${String(h).padStart(2, '0')}:30`);
}

const ClassSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeDay, setActiveDay] = useState('Monday');
  const [vacantDay, setVacantDay] = useState('Monday');
  const [vacantTime, setVacantTime] = useState('08:00');
  const [vacantResult, setVacantResult] = useState(null);
  const [activeTab, setActiveTab] = useState('schedule');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    fetchSchedules();
  }, []);

  const showMsg = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const res = await API.get('/schedules/all');
      setSchedules(res.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleCheckVacant = async () => {
    try {
      const res = await API.get(
        `/schedules/vacant?day=${vacantDay}&time=${vacantTime}`
      );
      setVacantResult(res.data);
    } catch (error) {
      showMsg('Error checking vacant members!', 'error');
    }
  };

  const daySchedules = schedules.filter(s => s.day === activeDay);

  // Group by member for the schedule view
  const memberSchedules = {};
  daySchedules.forEach(s => {
    const key = s.member_id;
    if (!memberSchedules[key]) {
      memberSchedules[key] = {
        name: `${s.first_name} ${s.last_name}`,
        school_id: s.school_id,
        classes: []
      };
    }
    memberSchedules[key].classes.push(s);
  });

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📅 Class Schedule</h2>
          <p style={styles.subtitle}>
            View all members class schedules
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={messageType === 'success' ? styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: 'schedule', label: '📅 Weekly Schedule' },
          { id: 'vacant', label: '🔍 Find Vacant Members' },
        ].map(tab => (
          <button
            key={tab.id}
            style={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* WEEKLY SCHEDULE */}
      {activeTab === 'schedule' && (
        <div>
          {/* Day Selector */}
          <div style={styles.daySelector}>
            {days.map(day => (
              <button
                key={day}
                style={activeDay === day ? styles.dayActive : styles.dayBtn}
                onClick={() => setActiveDay(day)}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>

          {loading ? (
            <p style={styles.loadingText}>Loading...</p>
          ) : Object.keys(memberSchedules).length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>📅</p>
              <p style={styles.emptyText}>
                No classes scheduled on {activeDay}
              </p>
            </div>
          ) : (
            <div style={styles.scheduleGrid}>
              {/* Time Column */}
              <div style={styles.timeColumn}>
                <div style={styles.timeHeader}>Time</div>
                {timeSlots.map(time => (
                  <div key={time} style={styles.timeCell}>
                    {time}
                  </div>
                ))}
              </div>

              {/* Member Columns */}
              {Object.values(memberSchedules).map((member, idx) => (
                <div key={idx} style={styles.memberColumn}>
                  <div style={styles.memberHeader}>
                    <p style={styles.memberHeaderName}>{member.name}</p>
                    <p style={styles.memberHeaderId}>{member.school_id}</p>
                  </div>
                  {timeSlots.map(time => {
                    const hasClass = member.classes.find(c => {
                      const start = c.start_time.slice(0, 5);
                      const end = c.end_time.slice(0, 5);
                      return start <= time && end > time;
                    });
                    return (
                      <div
                        key={time}
                        style={{
                          ...styles.scheduleCell,
                          background: hasClass ? '#e8f0fe' : 'white',
                          borderLeft: hasClass ?
                            '3px solid #0f3460' : '1px solid #f0f0f0',
                        }}
                      >
                        {hasClass && start_time_matches(hasClass, time) && (
                          <p style={styles.subjectLabel}>
                            {hasClass.subject}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VACANT MEMBERS */}
      {activeTab === 'vacant' && (
        <div style={styles.vacantBox}>
          <h3 style={styles.vacantTitle}>
            🔍 Find Available Members
          </h3>
          <p style={styles.vacantSubtitle}>
            Check who is free on a specific day and time
          </p>

          <div style={styles.vacantFilter}>
            <div style={styles.filterField}>
              <label style={styles.label}>Day</label>
              <select style={styles.input}
                value={vacantDay}
                onChange={e => setVacantDay(e.target.value)}>
                {days.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div style={styles.filterField}>
              <label style={styles.label}>Time</label>
              <select style={styles.input}
                value={vacantTime}
                onChange={e => setVacantTime(e.target.value)}>
                {timeSlots.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <button style={styles.checkBtn}
              onClick={handleCheckVacant}>
              Check
            </button>
          </div>

          {vacantResult && (
            <div style={styles.vacantResult}>
              <div style={styles.vacantSection}>
                <h4 style={styles.vacantSectionTitle}>
                  ✅ Vacant ({vacantResult.vacant.length})
                </h4>
                {vacantResult.vacant.length === 0 ? (
                  <p style={styles.noResult}>No vacant members</p>
                ) : (
                  vacantResult.vacant.map(m => (
                    <div key={m.id} style={styles.vacantMember}>
                      <div style={styles.vacantAvatar}>
                        {m.first_name?.charAt(0)}{m.last_name?.charAt(0)}
                      </div>
                      <div>
                        <p style={styles.vacantName}>
                          {m.first_name} {m.last_name}
                        </p>
                        <p style={styles.vacantId}>{m.school_id}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div style={styles.vacantSection}>
                <h4 style={styles.notVacantTitle}>
                  ❌ Has Class ({vacantResult.not_vacant.length})
                </h4>
                {vacantResult.not_vacant.length === 0 ? (
                  <p style={styles.noResult}>No members with class</p>
                ) : (
                  vacantResult.not_vacant.map(m => (
                    <div key={m.id} style={{
                      ...styles.vacantMember,
                      opacity: 0.6
                    }}>
                      <div style={{
                        ...styles.vacantAvatar,
                        background: '#e74c3c'
                      }}>
                        {m.first_name?.charAt(0)}{m.last_name?.charAt(0)}
                      </div>
                      <div>
                        <p style={styles.vacantName}>
                          {m.first_name} {m.last_name}
                        </p>
                        <p style={styles.vacantId}>{m.school_id}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper to check if this is the start time cell
const start_time_matches = (classItem, time) => {
  return classItem.start_time.slice(0, 5) === time;
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '20px',
    flexWrap: 'wrap', gap: '10px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  tabs: {
    display: 'flex', gap: '8px',
    marginBottom: '20px', flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 18px', background: 'white',
    border: '1.5px solid #ddd', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px', color: '#666',
  },
  tabActive: {
    padding: '8px 18px', background: '#0f3460',
    border: '1.5px solid #0f3460', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px',
    color: 'white', fontWeight: 'bold',
  },
  daySelector: {
    display: 'flex', gap: '8px',
    marginBottom: '20px', flexWrap: 'wrap',
  },
  dayBtn: {
    padding: '8px 16px', background: 'white',
    border: '1.5px solid #ddd', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px', color: '#666',
  },
  dayActive: {
    padding: '8px 16px', background: '#0f3460',
    border: '1.5px solid #0f3460', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px',
    color: 'white', fontWeight: 'bold',
  },
  loadingText: { textAlign: 'center', color: '#888', padding: '30px' },
  emptyState: { textAlign: 'center', padding: '50px 0' },
  emptyIcon: { fontSize: '50px', margin: '0 0 10px 0' },
  emptyText: { color: '#888', fontSize: '14px', margin: 0 },
  scheduleGrid: {
    display: 'flex', overflowX: 'auto',
    background: 'white', borderRadius: '16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  timeColumn: { minWidth: '70px', flexShrink: 0 },
  timeHeader: {
    height: '60px', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontWeight: 'bold', fontSize: '12px',
    color: '#888', borderBottom: '1px solid #f0f0f0',
    background: '#f8f9fa',
  },
  timeCell: {
    height: '40px', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', color: '#aaa',
    borderBottom: '1px solid #f0f0f0',
    background: '#f8f9fa',
  },
  memberColumn: { minWidth: '150px', flex: 1 },
  memberHeader: {
    height: '60px', display: 'flex',
    flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', borderBottom: '1px solid #f0f0f0',
    background: '#f8f9fa', padding: '5px',
  },
  memberHeaderName: {
    margin: 0, fontWeight: 'bold',
    fontSize: '12px', color: '#333',
    textAlign: 'center',
  },
  memberHeaderId: {
    margin: 0, fontSize: '10px',
    color: '#aaa', textAlign: 'center',
  },
  scheduleCell: {
    height: '40px', borderBottom: '1px solid #f0f0f0',
    padding: '2px 6px', display: 'flex',
    alignItems: 'center', overflow: 'hidden',
  },
  subjectLabel: {
    margin: 0, fontSize: '10px',
    color: '#0f3460', fontWeight: 'bold',
    whiteSpace: 'nowrap', overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  vacantBox: {
    background: 'white', borderRadius: '16px',
    padding: '25px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  vacantTitle: {
    margin: '0 0 5px 0', fontSize: '18px',
    fontWeight: 'bold', color: '#333',
  },
  vacantSubtitle: {
    margin: '0 0 20px 0', color: '#888', fontSize: '13px',
  },
  vacantFilter: {
    display: 'flex', gap: '12px',
    alignItems: 'flex-end', flexWrap: 'wrap',
    marginBottom: '20px',
  },
  filterField: { flex: 1, minWidth: '150px' },
  label: {
    display: 'block', fontSize: '13px',
    fontWeight: '600', color: '#444', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px',
    borderRadius: '10px', border: '1.5px solid #ddd',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  checkBtn: {
    padding: '11px 24px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
  },
  vacantResult: {
    display: 'flex', gap: '20px', flexWrap: 'wrap',
  },
  vacantSection: { flex: 1, minWidth: '200px' },
  vacantSectionTitle: {
    margin: '0 0 12px 0', fontSize: '15px',
    fontWeight: 'bold', color: '#2ecc71',
  },
  notVacantTitle: {
    margin: '0 0 12px 0', fontSize: '15px',
    fontWeight: 'bold', color: '#e74c3c',
  },
  noResult: { color: '#aaa', fontSize: '13px' },
  vacantMember: {
    display: 'flex', alignItems: 'center',
    gap: '10px', padding: '10px',
    background: '#f8f9fa', borderRadius: '10px',
    marginBottom: '8px',
  },
  vacantAvatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    background: '#2ecc71', color: 'white',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 'bold',
    fontSize: '13px', flexShrink: 0,
  },
  vacantName: {
    margin: '0 0 2px 0', fontWeight: 'bold',
    color: '#333', fontSize: '13px',
  },
  vacantId: { margin: 0, color: '#aaa', fontSize: '11px' },
};

export default ClassSchedule;